import { createEnum } from "@redotech/zod-util/zod-enum";
import escapeRegExp from "lodash/escapeRegExp";
import * as z from "zod";
import {
  AdvancedFilterType,
  createAdvancedFilterDataSchema,
  GenericFilterBuilder,
} from "./generic-advanced-filter-data";

/**
 * @deprecated Use ArrayStringFilterOperator instead. This is kept only for migration purposes.
 */
export enum StringFilterOperator {
  EQUALS = "=",
  NOT_EQUALS = "!=",
  CONTAINS = "contains",
}
const StringFilterOperatorSchema = createEnum(StringFilterOperator, {
  enumName: "StringFilterOperator",
});

const StringFilterValueSchema = z.string();

/**
 * @deprecated Use ArrayStringFilterDataSchema instead. This is kept only for migration purposes.
 */
export const StringFilterDataSchema = createAdvancedFilterDataSchema(
  AdvancedFilterType.STRING,
  StringFilterValueSchema,
  StringFilterOperatorSchema,
);

/**
 * @deprecated Use ArrayStringFilterData instead. This is kept only for migration purposes.
 */
export type StringFilterData = z.infer<typeof StringFilterDataSchema>;

/**
 * @deprecated Use ArrayStringFilterBuilder instead. This is kept only for migration purposes.
 */
export const StringFilterBuilder: GenericFilterBuilder<
  string,
  StringFilterOperator,
  StringFilterData
> = {
  type: AdvancedFilterType.STRING,
  valueSchema: StringFilterValueSchema,
  operatorSchema: StringFilterOperatorSchema,
  schema: StringFilterDataSchema,
  addOpensearchFilters: ({ filter, opensearchPath, keywordPath, compound }) => {
    if (!filter.value) {
      return;
    }

    switch (filter.operator) {
      case StringFilterOperator.EQUALS: {
        compound.filter.push({ term: { [opensearchPath]: filter.value } });
        break;
      }
      case StringFilterOperator.NOT_EQUALS: {
        compound.must_not.push({ term: { [opensearchPath]: filter.value } });
        break;
      }
      case StringFilterOperator.CONTAINS: {
        const truncatedSearch = filter.value.substring(0, 200).trim();

        if (truncatedSearch && keywordPath) {
          compound.filter.push({
            wildcard: {
              path: keywordPath,
              query: `*${escapeRegExp(truncatedSearch)}*`,
            },
          });
        }
        break;
      }
      // No default
    }
  },
  readFromString: (name, operatorValueEncoded, defaultFilter) => {
    try {
      const operatorValue = operatorValueEncoded.split("::");

      const operator = operatorValue?.[0];
      const encodedValue = operatorValue?.[1];

      if (!operator) {
        return defaultFilter;
      }

      const parsedOperator = StringFilterOperatorSchema.safeParse(operator);
      if (!parsedOperator.success) {
        console.warn("Invalid operator type");
        return undefined;
      }

      if (!encodedValue) {
        return {
          type: AdvancedFilterType.STRING,
          name,
          value: null,
          operator: parsedOperator.data,
        };
      }

      const decodedValue = decodeURIComponent(encodedValue);
      const valueParsed = StringFilterValueSchema.safeParse(decodedValue);

      if (!valueParsed.success) {
        console.warn("Invalid value type for StringFilter");
        return undefined;
      }

      return {
        type: AdvancedFilterType.STRING,
        name,
        value: valueParsed.data,
        operator: parsedOperator.data,
      };
    } catch (error) {
      console.error("Error parsing StringFilter", error);
      return undefined;
    }
  },
  writeToString: (filter: StringFilterData | undefined) => {
    try {
      if (!filter) {
        return "";
      }
      const { operator, value } = filter;
      const parsedValue = StringFilterValueSchema.safeParse(value);

      if (parsedValue.success) {
        return `${operator}::${encodeURIComponent(parsedValue.data)}`;
      } else {
        return `${operator}::`;
      }
    } catch (error) {
      console.error("Error writing StringFilter", error);
      return "";
    }
  },
  isPartial: (filter) => {
    return !filter.operator || filter.value == null;
  },
};
